/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { IAdminInterview } from 'src/api'

const ProgressWidth = 500

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 80%;
`

const ItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 40px;
  width: 100%;
  margin-bottom: 5px;
`

const ItemStatus = styled.div`
  font-size: 12px;
  font-weight: 500;
  width: 20%;
  display: flex;
  align-items: center;
  justify-content: end;
  padding-right: 10px;
`

const ItemProgress = styled.div<{ widthPct: number; background: string }>`
  height: 100%;
  width: ${({ widthPct }) => ProgressWidth * widthPct}px;
  margin-left: ${({ widthPct }) => (ProgressWidth - ProgressWidth * widthPct) / 2}px;
  background: ${({ background }) => background};
`

const ItemCount = styled(ItemStatus)`
  margin-left: 8px;
  justify-content: start;
  width: auto;
`

interface IFunnelItem {
  status: FunnelInterviewStatus
  count: number
}

enum FunnelInterviewStatus {
  INVITED = 'Invited',
  MATCHING = 'Order form submitted',
  MATCHED = 'Matched',
  SCHEDULED = 'Scheduled',
  COMPLETED = 'Completed',
  INCOMPLETE = 'Incomplete',
}

interface IProps {
  interviews: IAdminInterview[]
}

const DashboardFunnelComponent = ({ interviews }: IProps) => {
  const [funnelItems, setFunnelItems] = useState<IFunnelItem[]>([])
  const [maxCount, setMaxCount] = useState(0)

  const incCounters = (src: IFunnelItem[], status: FunnelInterviewStatus) => {
    let dItem = src.find((item) => item.status === status)
    if (!dItem) {
      dItem = {
        status,
        count: 0,
      }
      src.push(dItem)
    }
    dItem.count += 1
  }

  const getColor = (index: number) => {
    switch (index) {
      case 0:
        return '#dad1e9'
      case 1:
        return '#b5a7d5'
      case 2:
        return '#8d7cc3'
      case 3:
        return '#664ea7'
      case 4:
        return '#351c75'
      case 5:
        return '#d9d9d9'
      default:
        return '#000000'
    }
  }

  const getCountLabel = (index: number) => {
    const currValue = funnelItems[index].count
    if (index === 0) {
      return currValue
    }
    const prevValue = funnelItems[index - 1].count
    return `${currValue} (-${100 - Math.round((currValue / prevValue) * 100)}%)`
  }

  useEffect(() => {
    const newDItems: IFunnelItem[] = []
    interviews.forEach((interview) => {
      // invited
      if (interview.admin_status === 'Invited') {
        incCounters(newDItems, FunnelInterviewStatus.INVITED)
      }
      // matching
      if (
        interview.admin_status === 'Order form submitted' ||
        interview.admin_status === 'Pending expert availability' ||
        interview.admin_status === 'Expert availability provided'
      ) {
        incCounters(newDItems, FunnelInterviewStatus.MATCHING)
      }
      // matched
      if (interview.admin_status === 'Matched') {
        incCounters(newDItems, FunnelInterviewStatus.MATCHED)
      }
      // scheduled
      if (
        interview.admin_status === 'Scheduled' ||
        interview.admin_status === 'Started'
      ) {
        incCounters(newDItems, FunnelInterviewStatus.SCHEDULED)
      }
      // completed
      if (interview.admin_status === 'Completed') {
        incCounters(newDItems, FunnelInterviewStatus.COMPLETED)
      }
      // incomplete
      if (interview.admin_status === 'Incomplete') {
        incCounters(newDItems, FunnelInterviewStatus.INCOMPLETE)
      }
    })
    newDItems.sort((a, b) => (a.count < b.count ? 1 : -1))
    const newMax = Math.max.apply(
      null,
      newDItems.map((i) => i.count)
    )
    setMaxCount(newMax)
    setFunnelItems(newDItems)
  }, [interviews])

  return (
    <ItemsContainer>
      {funnelItems.map((dItem, index) => (
        <ItemContainer key={index}>
          <ItemStatus>{dItem.status}</ItemStatus>
          <ItemProgress widthPct={dItem.count / maxCount} background={getColor(index)} />
          <ItemCount>{getCountLabel(index)}</ItemCount>
        </ItemContainer>
      ))}
    </ItemsContainer>
  )
}

export default DashboardFunnelComponent
